import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { Section, SectionHeading, Reveal } from "@/components/primitives";
import { Icon } from "@/components/icons";
import { getContent, trackEvent } from "@/lib/api";
import { QCoreArchitecture, InsideQCore } from "@/components/qcore";
import { ArrowRight, Cpu } from "@phosphor-icons/react";

const CAPABILITIES = [
  { icon: "MagnifyingGlass", title: "Multi-Market Analysis", desc: "Monitor fragmented cryptocurrency markets and analyze pricing relationships across CEXs, DEXs, derivatives, and chains." },
  { icon: "StackSimple", title: "Parallel Strategy Analysis", desc: "Multiple arbitrage strategies can be evaluated simultaneously by the engine." },
  { icon: "Broadcast", title: "Automated Opportunity Detection", desc: "The engine continuously evaluates market data for potential opportunities as conditions change." },
  { icon: "Cpu", title: "Execution Infrastructure", desc: "Where configured and technically supported, the system can interact with connected trading infrastructure to execute qualifying strategies." },
  { icon: "Gauge", title: "Risk Parameters", desc: "The platform can incorporate configurable risk controls and execution constraints." },
  { icon: "Database", title: "Data Intelligence", desc: "Continuous market data and system activity provide information that can be used to improve models and optimization." },
];

export default function QCore() {
  const [modes, setModes] = useState([]);
  useEffect(() => {
    trackEvent("page_view", { page: "q-core" });
    getContent("deploy_modes").then(setModes).catch(() => {});
  }, []);

  return (
    <div>
      <PageHeader
        eyebrow="Inside Q-Core"
        title="The proprietary engine behind Quantum Scalp."
        sub="Q-Core is the software engine that powers the Quantum Scalp platform. It analyzes fragmented markets, evaluates strategies in parallel, and — where configured — interacts with connected trading infrastructure."
      >
        <Link to="/pricing" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#2DD4BF] text-[#030305] font-semibold hover:bg-white transition-colors" data-testid="qcore-cta">
          Access Q-Core <ArrowRight size={16} weight="bold" />
        </Link>
      </PageHeader>

      <Section grid>
        <SectionHeading
          eyebrow="System Architecture"
          title="From market data to portfolio — end to end."
          sub="Hover or tap any stage to understand how Q-Core moves from raw market data to a verifiable portfolio result."
          className="mb-12"
        />
        <QCoreArchitecture />
      </Section>

      <Section grid>
        <SectionHeading
          align="center" eyebrow="Inside Q-Core"
          title="A deterministic pipeline, running continuously."
          className="mb-12"
        />
        <InsideQCore />
      </Section>

      <Section grid>
        <SectionHeading eyebrow="Core Capabilities" title="What the engine is designed to do." className="mb-12" />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {CAPABILITIES.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.05}>
              <div className="h-full rounded-xl border border-white/10 bg-[#0A0B10] p-6 hover:border-[#2DD4BF]/30 transition-colors" data-testid={`capability-${i}`}>
                <span className="grid place-items-center h-11 w-11 rounded-lg border border-white/10 bg-white/[0.03] text-[#2DD4BF] mb-5">
                  <Icon name={c.icon} size={22} weight="duotone" />
                </span>
                <h3 className="text-lg font-bold text-white mb-2">{c.title}</h3>
                <p className="text-sm text-[#94A3B8] leading-relaxed">{c.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section grid>
        <div className="rounded-2xl border border-[#0E9E90]/25 bg-[#0E9E90]/[0.05] p-8 md:p-12">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#a5b0f5] mb-4">On Terminology</p>
          <h3 className="text-2xl md:text-3xl font-black text-white max-w-3xl">
            Quantum-inspired optimization, running on classical computing infrastructure.
          </h3>
          <p className="mt-5 text-[#cbd5e1] max-w-3xl leading-relaxed">
            Q-Core uses concepts inspired by quantum optimization while operating on conventional computing
            infrastructure. Q-Core is <span className="text-white font-semibold">not</span> a literal quantum computer.
            We avoid scientifically misleading statements and describe the technology as it actually works.
          </p>
        </div>
      </Section>

      <Section grid>
        <SectionHeading
          eyebrow="Deployment Configurations"
          title="Two ways to deploy your license."
          sub="Choose how your Q-Core license operates. Both configurations involve trading risk."
          className="mb-12"
        />
        <div className="grid md:grid-cols-2 gap-6">
          {modes.map((m, i) => (
            <Reveal key={m.id} delay={i * 0.08}>
              <div className="h-full rounded-xl border border-white/10 bg-[#0A0B10] p-7" data-testid={`deploy-mode-${m.slug}`}>
                <div className="flex items-center gap-3 mb-5">
                  <span className="grid place-items-center h-12 w-12 rounded-lg border border-[#2DD4BF]/30 bg-[#2DD4BF]/10 text-[#2DD4BF]">
                    <Icon name={m.icon} size={24} weight="duotone" />
                  </span>
                  <div>
                    <h3 className="text-xl font-bold text-white">{m.name}</h3>
                    <p className="text-sm text-[#94A3B8]">{m.tagline}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {(m.features || []).map((f) => (
                    <div key={f.title} className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
                      <p className="text-sm font-semibold text-white">{f.title}</p>
                      <p className="text-sm text-[#94A3B8] mt-1">{f.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>
    </div>
  );
}
